import React, { useRef } from 'react'
import { RedtailWorkflow, RedtailWorkflowTemplate } from '../../../../../../../models/Integration'
import { User } from '../../../../../../../models/User'
import { useAppDispatch, useAppSelector } from '../../../../../../../store/hooks'
import { selectAccountId } from '../../../../../../../store/auth/authSlice'
import { TrackEvent, useTracking } from '../../../../common/tracking'
import { Controller, useForm } from 'react-hook-form'
import { useCreateIntegrationEntityMutation } from '../../../../../../../store/integrations/integrations'
import { useOutsideClick } from '../../../../../../../common/useOutsideClick'
import { useOverlayContainer } from '../../../../../../../common/useOverlayContainer'
import { addToast } from '../../../../../../../store/toast/toastSlice'
import { Form, Overlay } from 'react-bootstrap'
import DatePicker from 'react-datepicker'
import { Button } from '../../../../../../../components/Button/Button'

interface StartWorkflowOverlayProps {
  targetRef: React.RefObject<HTMLElement | null>
  show: boolean
  onHide: () => void
  onWorkflowStarted?: (workflow: RedtailWorkflow) => void
  workflowTemplate: RedtailWorkflowTemplate
  contact: User
  serviceId: string
  suggestedLabel?: string
}

interface StartWorkflowFormValues {
  name: string
  targetDate: Date
  contactId?: string
}

export const StartRedtailWorkflowOverlay: React.FC<StartWorkflowOverlayProps> = ({
  targetRef,
  show,
  onHide,
  onWorkflowStarted,
  workflowTemplate,
  contact,
  serviceId,
  suggestedLabel,
}) => {
  const accountId = useAppSelector(selectAccountId)
  const dispatch = useAppDispatch()
  const { tracked } = useTracking()

  const integration = contact.integrations!.find((i) => i.serviceId === serviceId)
  const defaultValues = {
    name: suggestedLabel || workflowTemplate.name,
    contactId: integration?.externalId,
    targetDate: new Date(),
  }

  const { control, handleSubmit, reset } = useForm<StartWorkflowFormValues>({ defaultValues })

  const overlayRef = useRef(null)
  const overlayContainer = useOverlayContainer(targetRef)

  const [triggerCreateIntegrationEntity, { isLoading: isCreatingWorkflow }] = useCreateIntegrationEntityMutation()

  useOutsideClick(overlayRef, () => {
    onHide()
  })

  const _onStartWorkflow = async (formValues: StartWorkflowFormValues) => {
    try {
      const { data: workflow } = await triggerCreateIntegrationEntity({
        accountId,
        connector: serviceId,
        entityType: 'workflow',
        relatedTo: {
          id: formValues.contactId!,
        },
        payload: {
          workflow_template_id: workflowTemplate.redtailOneId,
          name: formValues.name,
          due_date: formValues.targetDate,
          contact_id: formValues.contactId,
        },
      }).unwrap()
      dispatch(addToast({ variant: 'success', title: 'Success', messages: ['The workflow has started successfully.'] }))
      reset()
      onWorkflowStarted?.({
        id: workflow.id,
        activeStep: workflow.activeStep,
        name: workflow.name,
        description: workflow.description,
        label: workflow.label,
      } as RedtailWorkflow)
    } catch {
      // Failed API calls are tracked centrally; nothing else to report here
    }
  }

  const onStartWorkflow = tracked(
    _onStartWorkflow,
    {
      eventName: TrackEvent.START_NEW_WORKFLOW_IN_CRM,
    },
    {
      eventDetailsExtractor: (formValues: StartWorkflowFormValues) => {
        return {
          connector: serviceId,
          contactId: contact.id,
          name: formValues.name,
          targetDate: formValues.targetDate,
        }
      },
    },
  )

  return (
    <Overlay ref={overlayRef} target={targetRef} container={overlayContainer} show={show} placement='left'>
      {({ ref, style }) => (
        <div ref={ref} style={style} className='start-workflow-overlay d-flex flex-column gap-3 border-default zocks-border-0.25 p-4'>
          <h4>Start {workflowTemplate.name}</h4>
          <Controller
            name='name'
            control={control}
            render={({ field }) => (
              <div>
                <Form.Label>Workflow name</Form.Label>
                <Form.Control id={field.name} placeholder='Label…' {...field} value={field.value} onChange={field.onChange} />
              </div>
            )}
          />
          <Controller
            name='targetDate'
            control={control}
            render={({ field }) => (
              <div className='d-flex flex-column'>
                <Form.Label>Target Date</Form.Label>
                <DatePicker
                  className='form-control'
                  selected={field.value}
                  onChange={(date) => field.onChange(date)}
                  todayButton='Today'
                  showTimeSelect
                  dateFormat='MM/dd/yyyy h:mm aa'
                  placeholderText='Starts at…'
                />
              </div>
            )}
          />
          <div className='align-self-end d-flex gap-2'>
            <Button variant='secondary' className='button-small' onClick={onHide}>
              Cancel
            </Button>
            <Button variant='primary' className='button-small' onClick={handleSubmit(onStartWorkflow)} loading={isCreatingWorkflow}>
              <i className='bi bi-play' />
              Start
            </Button>
          </div>
        </div>
      )}
    </Overlay>
  )
}
